import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Pencil } from "lucide-react";
import { StatusBadge, DATE_FIELDS, fmtDate, isPastDue } from "./equipmentMeta";
import { HistoryLog } from "./HistoryLog";
import { RequestStatusForm } from "./StatusRequests";
import { EquipmentPhotos } from "./EquipmentPhotos";

const money = (v) => (v == null || v === "" ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const Row = ({ label, value, testid, alert }) => (
  <div className="py-1.5 min-w-0">
    <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">{label}</div>
    <div data-testid={testid} className={`text-sm whitespace-pre-wrap break-words ${alert ? "text-red-600 font-bold" : "text-zinc-900"}`}>{value || "—"}</div>
  </div>
);

export const EquipmentDetailDialog = ({ equipment: e, onClose, onEdit, onChange, canManage, canLog, canDelete, canRequest, pendingRequest, onRequested }) => (
  <Dialog open={!!e} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="rounded-none max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="equipment-detail-dialog">
      {e && (
        <>
          <DialogHeader>
            <div className="flex items-start justify-between gap-3 pr-6">
              <div className="min-w-0">
                {e.asset_id && <div className="text-xs font-mono font-bold text-brand" data-testid="detail-asset-id">{e.asset_id}</div>}
                <DialogTitle className="font-heading text-2xl font-black tracking-tighter break-words">{e.name}</DialogTitle>
              </div>
              <StatusBadge status={e.status} testid="detail-status" />
            </div>
          </DialogHeader>
          <EquipmentPhotos equipment={e} canManage={canLog} onChange={onChange} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
            <div className="sm:col-span-2"><Row label="Equipment description" value={e.description} testid="detail-description" /></div>
            <Row label="Category" value={e.category} testid="detail-category" />
            <Row label="Make and model" value={e.make_model} testid="detail-make-model" />
            <Row label="Serial number" value={e.serial_number} testid="detail-serial" />
            <Row label="WLL / Capacity / Rating" value={e.wll} testid="detail-wll" />
            <Row label="Weight and dimensions" value={e.weight_dimensions} testid="detail-weight" />
            <Row label="Asset owner" value={e.asset_owner} testid="detail-owner" />
            <Row label="Location" value={e.location} testid="detail-location" />
            <Row label="Stock" value={`${e.available} / ${e.quantity} available`} testid="detail-stock" />
            <Row label="Replacement value" value={money(e.replacement_value)} testid="detail-replacement" />
            <Row label="Rates (day / week / month)" value={`${money(e.daily_rate)} / ${money(e.weekly_rate)} / ${money(e.monthly_rate)}`} testid="detail-rates" />
            {DATE_FIELDS.map(([k, label]) => (
              <Row key={k} label={label} value={e[k] ? fmtDate(e[k]) : ""} testid={`detail-${k}`}
                alert={k !== "last_inspection_date" && isPastDue(e[k])} />
            ))}
            <div className="sm:col-span-2"><Row label="Accessories included" value={e.accessories} testid="detail-accessories" /></div>
            <div className="sm:col-span-2"><Row label="Revenue splits" value={e.revenue_splits} testid="detail-revenue-splits" /></div>
          </div>
          <div className="space-y-5 mt-2">
            {canRequest && <RequestStatusForm equipment={e} pending={pendingRequest} onRequested={onRequested} />}
            <HistoryLog equipment={e} kind="damage" title="Damage history" canAdd={canLog} canDelete={canDelete} onChange={onChange} />
            <HistoryLog equipment={e} kind="maintenance" title="Maintenance history" canAdd={canLog} canDelete={canDelete} onChange={onChange} />
          </div>
          {canManage && (
            <button onClick={() => onEdit(e)} data-testid="detail-edit-button"
              className="mt-2 self-start bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors px-5 py-2 flex items-center gap-2 text-sm">
              <Pencil size={14} /> Edit details
            </button>
          )}
        </>
      )}
    </DialogContent>
  </Dialog>
);
